import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  Input,
  InputSignal,
  OnInit,
} from '@angular/core';

import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateService } from '@ngx-translate/core';
import { ContextService } from 'app/core/services/context.service';
import { TrailerLocationType } from 'app/shared/enums';
import { DoorType } from 'app/shared/enums/door-type.enum';
import { SitesService, TrailerApiService, TrailersService } from 'app/shared/services';
import { FeatureFlags } from 'app/shared/services/feature-flags/feature-flags.const';
import { ApiResponse, SiteLocation } from 'app/shared/types';
import { isInvalidArea } from 'app/shared/utility/location-utils';
import { BehaviorSubject, combineLatest, map, Observable, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AutocompleteComponent } from '../../atoms/autocomplete/autocomplete.component';
import { IAutocompleteItem, InputAutocompleteComponent } from '../../molecules';

@Component({
  selector: 'app-location-select',
  templateUrl: './location-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinner, InputAutocompleteComponent, AutocompleteComponent, AsyncPipe],
})
export class LocationSelectComponent implements OnInit {
  @Input() public controlName!: string;
  @Input() public form!: FormGroup;
  @Input() public label = 'Location select component';
  @Input() public set defaultLocation(v: TrailerLocationType | null) {
    this.defaultLocation$.next(v as number);
  }
  @Input() public ariaLabel = 'Location select component';
  @Input() public isSiteManaged = false;
  @Input() public checkTrailers = true; // TODO: remove this when the feature flag shouldHandleOccupiedDoors is removed
  @Input() public taskType: string | null = null;
  @Input() public doorType: DoorType = DoorType.LOCATION_TO;

  public filteredLocations: IAutocompleteItem<string>[] = [];
  public hiddenLocations: InputSignal<TrailerLocationType[] | undefined> = input();
  private hiddenLocations$ = toObservable(this.hiddenLocations);
  protected readonly defaultLocation$ = new BehaviorSubject(0);

  private cdr = inject(ChangeDetectorRef);
  private sitesService = inject(SitesService);
  private contextService = inject(ContextService);
  private trailersService = inject(TrailersService);
  private trailerApiService = inject(TrailerApiService);
  private destroyRef = inject(DestroyRef);
  private translateSvc = inject(TranslateService);
  public isLoadingSiteLocations = true;
  public siteLocations$ = this.contextService.siteId.pipe(
    switchMap((siteId) =>
      siteId ? this.sitesService.getSiteLocations(siteId, this.isLocationsHierarchyEnabled) : of(null),
    ),
    takeUntilDestroyed(this.destroyRef),
  );

  // This api is called either way however it will return occupied doors only if the feature flag is enabled on backend
  // otherwise it will return all available locations so it is weird
  public trailerLocations$ = this.contextService.siteId.pipe(
    switchMap((siteId) => (siteId ? this.getLocations(siteId) : of(null))),
    takeUntilDestroyed(this.destroyRef),
  );

  private useYardOpsApi = this.contextService.isFeatureFlagEnabled(FeatureFlags.UseYardOpsApi);
  public shouldHandleOccupiedDoors = this.contextService.isFeatureFlagEnabled(FeatureFlags.OccupiedDoors);
  public isLocationsHierarchyEnabled = this.contextService.isFeatureFlagEnabled(FeatureFlags.LocationsHierarchy);

  //TODO: remove this when the feature flag shouldHandleOccupiedDoors is removed
  public filteredLocations$ = combineLatest([
    this.siteLocations$,
    this.hiddenLocations$,
    this.defaultLocation$,
    this.trailerLocations$,
  ]).pipe(
    map(([res, hiddenLocs, defaultLocation, trailerLocations]) => {
      let filtered: SiteLocation[] = [];
      const locations = res?.value?.locations || [];
      const trailerLocs = trailerLocations?.value || [];

      if (!this.isSiteManaged || !this.checkTrailers) {
        filtered = locations.filter(({ locationTypeId }) => !hiddenLocs?.includes(locationTypeId)) || [];
      } else {
        const intersection = locations.filter((loc) =>
          trailerLocs.some((tLoc) => tLoc.id === loc.id || loc.id === this.form?.get(this.controlName)?.value),
        );
        filtered = intersection.filter(({ locationTypeId }) => !hiddenLocs?.includes(locationTypeId));
      }

      return { filtered, defaultLocation };
    }),
    tap(({ filtered, defaultLocation }) => {
      if (defaultLocation && filtered?.length) {
        const foundDefaultLocation = filtered.find((loc) => loc.locationTypeId === defaultLocation && loc.isDefault);

        const foundNonDefaultLocation = filtered.find(
          (loc) => loc.locationTypeId === defaultLocation && !loc.isDefault,
        );

        const foundLocation = foundDefaultLocation ? foundDefaultLocation : foundNonDefaultLocation;

        if (foundLocation) {
          this.inputControl?.setValue(foundLocation?.id);
        }
      }
    }),
    map(({ filtered }) =>
      filtered.map((f) => ({
        value: f.id,
        label: f.name,
      })),
    ),
  );

  public allLocations$ = combineLatest([this.siteLocations$, this.hiddenLocations$, this.trailerLocations$]).pipe(
    map(([siteRes, hiddenLocs, trailerRes]) => {
      const locations =
        siteRes?.value?.locations.filter(({ locationTypeId }) => !hiddenLocs?.includes(locationTypeId)) ?? [];

      const trailerLocations = trailerRes?.value ?? [];

      //TODO: remove this to be in positive check
      const shouldNotIntersectLocations = !this.isSiteManaged || !this.checkTrailers;

      const intersection = shouldNotIntersectLocations
        ? locations
        : locations.filter((loc) =>
            trailerLocations.some((tLoc) => tLoc.id === loc.id || loc.id === this.form?.get(this.controlName)?.value),
          );

      return intersection.map((loc) => {
        const invalidArea = isInvalidArea(loc, this.isLocationsHierarchyEnabled);
        const parentLocationName = this.isLocationsHierarchyEnabled
          ? siteRes?.value?.locations.find((x) => x.id === loc.parentId)?.name
          : '';

        const localizedLocationTypeName = this.translateSvc.instant(
          `yardControl.lists.location.${loc.locationTypeCode}`,
        );

        return {
          ...loc,
          label: `${loc.name} (${localizedLocationTypeName})`,
          sublabel: this.isLocationsHierarchyEnabled ? parentLocationName : undefined,
          disabled: invalidArea,
        };
      });
    }),
    withLatestFrom(this.defaultLocation$),
    tap(([locations, defaultLoc]) => {
      if (defaultLoc && locations.length) {
        const foundLocation =
          locations.find((loc) => loc.locationTypeId === defaultLoc && loc.isDefault) ||
          locations.find((loc) => loc.locationTypeId === defaultLoc);
        if (foundLocation) {
          this.inputControl?.setValue(foundLocation.id);
        }
      }
    }),
    map(([locations]) => locations),
  );

  public ngOnInit(): void {
    this.filteredLocations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((r) => {
      this.isLoadingSiteLocations = false;
      this.filteredLocations = r;
      this.cdr.detectChanges();
    });
  }

  public get inputControl(): AbstractControl<string> | null {
    return this.form.get(this.controlName);
  }

  private getLocations(siteId: string): Observable<ApiResponse<SiteLocation[]>> {
    if (this.useYardOpsApi) {
      return this.trailerApiService.getAvailableLocations();
    }
    return this.trailersService.getTrailerLocations(siteId);
  }
}
